import React, { useState } from "react";
import { FiArrowUpRight, FiBarChart2, FiInfo } from "react-icons/fi";
import { Link } from "react-router-dom";
import CoinIcon from "../components/CoinIcon";
import { useBinanceTicker, useBinanceTickers } from "../services/BinanceTickerContext";
import { marketAsset, marketChange, marketName, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";

function TradingMarketRow({ market, rank }) {
  const symbol = marketSymbol(market);
  const ticker = useBinanceTicker(symbol);
  const change = marketChange(ticker);
  const detailPath = `/markets/asset/${encodeURIComponent(symbol)}`;
  const asset = marketAsset(market);

  return (
    <div className="trading-market-row" role="row">
      <span className="trading-market-name" role="cell">
        <span className="trading-market-rank">{rank}</span>
        <CoinIcon symbol={symbol} logo={asset.logo} name={marketName(market)} />
        <span><b>{symbol}</b><small>{marketName(market)}</small></span>
      </span>
      <span role="cell">{marketPrice(ticker)}</span>
      <span className={change.startsWith("+") ? "up" : change === "—" ? "" : "down"} role="cell">{change}</span>
      <span role="cell">{ticker?.volume == null ? "—" : ticker.volume.toLocaleString("en-US")}</span>
      <span className="trading-market-actions" role="cell">
        <Link to={`/trade?symbol=${encodeURIComponent(market.market_symbol ?? market.symbol ?? `${symbol}USDT`)}`} aria-label={`Trade ${symbol}`} title={`Trade ${symbol}`}><FiBarChart2 /></Link>
        <Link to={detailPath} aria-label={`View ${symbol} details`} title={`View ${symbol} details`}><FiInfo /></Link>
      </span>
    </div>
  );
}

const TradingData = () => {
  const { assets, loading, error } = useMarketAssets();
  const tickers = useBinanceTickers();
  const [search, setSearch] = useState("");
  const [quoteAsset, setQuoteAsset] = useState("all");
  const quoteAssets = [...new Set(assets.map((market) => market.quote_asset).filter(Boolean))];
  const filteredMarkets = assets
    .filter((market) => quoteAsset === "all" || market.quote_asset === quoteAsset)
    .filter((market) => `${marketSymbol(market)} ${marketName(market)}`.toLowerCase().includes(search.toLowerCase()))
    .sort((left, right) => (tickers[marketSymbol(right)]?.volume ?? 0) - (tickers[marketSymbol(left)]?.volume ?? 0));

  return (
    <section className="market-data-page">
      <div className="market-data-heading">
        <div><h1>Trading Data</h1><p>Live market performance for admin-enabled trading pairs.</p></div>
        <Link to="/markets/overview">Markets overview <FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      <div className="market-data-filters">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search assets" aria-label="Search assets" />
        <select value={quoteAsset} onChange={(event) => setQuoteAsset(event.target.value)} aria-label="Filter by quote asset">
          <option value="all">All quote assets</option>
          {quoteAssets.map((quote) => <option key={quote} value={quote}>{quote}</option>)}
        </select>
      </div>
      {error && <p className="market-state market-state-error" role="alert">{error}</p>}
      <div className="trading-market-table" role="table" aria-label="Trading data">
        <div className="trading-market-head" role="row"><span role="columnheader">Asset</span><span role="columnheader">Price</span><span role="columnheader">24h Change</span><span role="columnheader">24h Volume</span><span role="columnheader">Actions</span></div>
        {filteredMarkets.map((market, index) => <TradingMarketRow key={marketSymbol(market)} market={market} rank={index + 1} />)}
        {!loading && !error && filteredMarkets.length === 0 && <p className="market-state">No markets match this filter.</p>}
        {loading && <p className="market-state">Loading trading data…</p>}
      </div>
    </section>
  );
};

export default TradingData;
